import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const FAQEditor = () => {
  const [faqs, setFaqs] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    order: 0
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/faq`);
      setFaqs(response.data);
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingFaq) {
        await axios.put(`${BACKEND_URL}/api/admin/faq/${editingFaq.id}`, formData);
        toast({ title: 'Success', description: 'FAQ updated successfully' });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/faq`, formData);
        toast({ title: 'Success', description: 'FAQ created successfully' });
      }
      setIsOpen(false);
      resetForm();
      fetchFaqs();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save FAQ', variant: 'destructive' });
    }
  };

  const handleEdit = (faq) => {
    setEditingFaq(faq);
    setFormData(faq);
    setIsOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/faq/${id}`);
      toast({ title: 'Success', description: 'FAQ deleted successfully' });
      fetchFaqs();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete FAQ', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({ question: '', answer: '', order: 0 });
    setEditingFaq(null);
  };

  return (
    <Card className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} className="bg-pink-500 hover:bg-pink-600">
              <Plus className="w-4 h-4 mr-2" />
              Add FAQ
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingFaq ? 'Edit FAQ' : 'Add FAQ'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Question</Label>
                <Input
                  value={formData.question}
                  onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                  placeholder="Enter question"
                />
              </div>
              <div>
                <Label>Answer</Label>
                <Textarea
                  value={formData.answer}
                  onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                  placeholder="Enter answer"
                  rows={4}
                />
              </div>
              <div>
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                />
              </div>
              <Button onClick={handleSubmit} className="w-full">
                {editingFaq ? 'Update' : 'Create'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <Card key={faq.id} className="p-6">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-3">
                  <span className="text-pink-500 font-bold text-lg">{index + 1}</span>
                  <h3 className="font-bold text-lg">{faq.question}</h3>
                </div>
                <p className="text-gray-700 ml-8">{faq.answer}</p>
                <p className="text-xs text-gray-500 mt-3 ml-8">Order: {faq.order}</p>
              </div>
              <div className="flex space-x-2 ml-4">
                <Button size="sm" variant="outline" onClick={() => handleEdit(faq)}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(faq.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {faqs.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No FAQs added yet
        </div>
      )}
    </Card>
  );
};

export default FAQEditor;
